import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useGetWithdrawalReceiptSettingsQuery,
    useUpdateWithdrawalReceiptSettingsMutation,
} from "../api";
import type { WithdrawalReceiptSettings } from "../api/withdrawalReceipts";

const defaultSettings: WithdrawalReceiptSettings = {
    main_enabled: true,
    milennium_enabled: true,
};

const WithdrawalReceiptSettingsPage: React.FC = () => {
    const { t } = useTranslation("common");
    const {
        data,
        isLoading,
        isError,
        refetch,
    } = useGetWithdrawalReceiptSettingsQuery();
    const [updateSettings, { isLoading: isSaving }] =
        useUpdateWithdrawalReceiptSettingsMutation();
    const [settings, setSettings] =
        useState<WithdrawalReceiptSettings>(defaultSettings);
    const [saveState, setSaveState] = useState<"idle" | "saved" | "error">(
        "idle"
    );

    useEffect(() => {
        if (data?.data) {
            setSettings(data.data);
        }
    }, [data]);

    const setEnabled = (
        key: keyof WithdrawalReceiptSettings,
        enabled: boolean
    ) => {
        setSaveState("idle");
        setSettings((current) => ({
            ...current,
            [key]: enabled,
        }));
    };

    const save = async () => {
        setSaveState("idle");

        try {
            const response = await updateSettings(settings).unwrap();
            setSettings(response.data);
            setSaveState("saved");
        } catch {
            setSaveState("error");
        }
    };

    if (isLoading) {
        return (
            <div className="p-6 text-sm text-gray-600 dark:text-gray-400">
                {t("withdrawal_receipts.loading")}
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="max-w-3xl space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("withdrawal_receipts.title")}
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        {t("withdrawal_receipts.description")}
                    </p>
                </div>

                {isError && (
                    <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
                    >
                        <span>{t("withdrawal_receipts.load_error")}</span>
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="ml-2 font-semibold underline"
                        >
                            {t("withdrawal_receipts.retry")}
                        </button>
                    </div>
                )}

                <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
                    <div className="space-y-5">
                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox"
                                checked={settings.main_enabled}
                                onChange={(event) =>
                                    setEnabled(
                                        "main_enabled",
                                        event.target.checked
                                    )
                                }
                                disabled={isSaving || isError}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                            />
                            <span>
                                <span className="block font-semibold text-gray-900 dark:text-white">
                                    @ALNY_market_bot
                                </span>
                                <span className="mt-1 block text-sm text-gray-600 dark:text-gray-400">
                                    {t(
                                        "withdrawal_receipts.main_bot_description"
                                    )}
                                </span>
                            </span>
                        </label>

                        <div className="border-t border-gray-200 dark:border-gray-700" />

                        <label className="flex cursor-pointer items-start gap-3">
                            <input
                                type="checkbox"
                                checked={settings.milennium_enabled}
                                onChange={(event) =>
                                    setEnabled(
                                        "milennium_enabled",
                                        event.target.checked
                                    )
                                }
                                disabled={isSaving || isError}
                                className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                            />
                            <span>
                                <span className="block font-semibold text-gray-900 dark:text-white">
                                    @milennium_bot
                                </span>
                                <span className="mt-1 block text-sm text-gray-600 dark:text-gray-400">
                                    {t(
                                        "withdrawal_receipts.milennium_bot_description"
                                    )}
                                </span>
                            </span>
                        </label>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={save}
                            disabled={isSaving || isError || !data}
                            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isSaving
                                ? t("withdrawal_receipts.saving")
                                : t("withdrawal_receipts.save")}
                        </button>

                        {saveState === "saved" && (
                            <span
                                role="status"
                                className="text-sm font-medium text-green-600 dark:text-green-400"
                            >
                                {t("withdrawal_receipts.saved")}
                            </span>
                        )}
                        {saveState === "error" && (
                            <span
                                role="alert"
                                className="text-sm font-medium text-red-600 dark:text-red-400"
                            >
                                {t("withdrawal_receipts.save_error")}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WithdrawalReceiptSettingsPage;
